import { api } from '@agency/shared';
import { Chip, DataTable, PageHeader, type DataTableColumn } from '@agency/ui';
import { useEffect, useMemo, useState } from 'react';
import { ErrorCallout, IdCell, Page, SensitiveButton, Stamp, TableCard, UserLink } from '../../app/widgets.js';
import { ApiError, call } from '../../lib/api.js';
import { useMutation } from '../../lib/mutate.js';
import { useApi } from '../../lib/useApi.js';

type Row = api.Session & { userDisplayName: string };

/** Active sessions across staff (aggregated from the per-person session route). */
export function SessionsPage() {
  const mutation = useMutation();
  const people = useApi('admin.people.list', { input: { limit: 200 } });
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!people.data) return;
    let cancelled = false;
    const candidates = people.data.items.filter((u) => u.status !== 'removed');
    void (async () => {
      try {
        const results = await Promise.all(candidates.map(async (u) => (await call('admin.people.sessions.list', { params: { userId: u.id } })).data.items.map((s) => ({ ...s, userDisplayName: u.displayName }))));
        if (cancelled) return;
        const now = Date.now();
        setRows(results.flat().filter((s) => !s.revokedAt && new Date(s.expiresAt).getTime() > now).sort((a, b) => b.lastActiveAt.localeCompare(a.lastActiveAt)));
        setError(null);
      } catch (e) {
        if (!cancelled) setError(ApiError.from(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [people.data, tick]);

  const columns = useMemo<DataTableColumn<Row>[]>(
    () => [
      { id: 'user', header: 'User', cell: ({ row }) => <UserLink userId={row.original.userId} fallback={row.original.userDisplayName} />, size: 180 },
      { id: 'kind', header: 'Kind', cell: ({ row }) => <Chip tone={row.original.kind === 'admin' ? 'info' : 'mute'}>{row.original.kind}</Chip>, size: 90 },
      { id: 'client', header: 'Client', cell: ({ row }) => <span>{row.original.clientSummary ?? '--'}</span>, size: 220 },
      { id: 'device', header: 'Device', cell: ({ row }) => <IdCell value={row.original.deviceId} />, size: 120 },
      { id: 'created', header: 'Created', cell: ({ row }) => <Stamp value={row.original.createdAt} />, size: 150 },
      { id: 'recent', header: 'Recent auth', cell: ({ row }) => <Stamp value={row.original.recentAuthAt} relative />, size: 110 },
      { id: 'active', header: 'Last active', cell: ({ row }) => <Stamp value={row.original.lastActiveAt} relative />, size: 110 },
      { id: 'expires', header: 'Expires', cell: ({ row }) => <Stamp value={row.original.expiresAt} relative />, size: 100 },
      { id: 'actions', header: '', size: 100, cell: ({ row }) => <SensitiveButton size="sm" variant="tertiary" onClick={async () => { const r = await mutation.run('admin.sessions.revoke', { params: { sessionId: row.original.id }, input: {} }, { title: 'Session revoked', invalidate: ['admin.people'] }); if (r) setTick((t) => t + 1); }}>Revoke</SensitiveButton> },
    ],
    [mutation],
  );

  const list = rows ?? [];
  return (
    <Page>
      <PageHeader eyebrow="People and access" eyebrowPath="Sessions" title="Sessions" subtitle={rows ? `${list.length} active sessions · ${list.filter((s) => s.kind === 'desktop').length} desktop · ${list.filter((s) => s.kind === 'admin').length} admin` : 'Loading sessions for every active person.'} />
      <ErrorCallout error={people.error ?? error} onRetry={() => setTick((t) => t + 1)} className="mt-6" />
      <div className="mt-6">
        <TableCard title="Active sessions" subtitle="Revoked and expired sessions are listed per person">
          <DataTable<Row> aria-label="Active sessions" columns={columns} rows={list} rowKey={(r) => r.id} height={Math.min(640, Math.max(200, list.length * 34 + 40))} state={rows === null ? 'loading' : list.length === 0 ? 'empty' : 'complete'} emptyMessage="No active sessions." noun="sessions" />
        </TableCard>
      </div>
    </Page>
  );
}
